import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import PhoneInput from '../../Common/PhoneInput';
import { getRenderFamily } from '../../../utils/applicationQuestionTypes';
import { isPurePrintDeclaration, resolveTypeConfig } from '../../../utils/applicationQuestionTypeConfig';
import {
  countWords,
  formatDisplayValue,
  getOtherOptionLabel,
  getPercentageLimits,
  getWordCountLimits,
  isOtherOptionValue,
  normalizeMultipleChoiceOtherValue,
  normalizeSingleChoiceOtherValue,
  percentageRangeError,
  resolvePipedSourceValue,
  wordCountError,
} from '../../../utils/applicationQuestionValidation';
import {
  DeclarationBlock,
  isDeclarationQuestion,
  isStaticContentQuestion,
  SignaturePadPreview,
  validateDeclarationValue,
} from './applicationQuestionRenderers';
import { coerceAnswerValueForQuestion, isComplexAnswerEmpty } from '../../../utils/applicationAnswerCoercion';
import { dataGridColumns, isDataGridAnswerEmpty } from '../../../utils/dataGridAnswerDisplay';
import { dataGridRowCount } from '../../../utils/dataGridQuestionOptions';
import { openFileInNewTab, downloadFileAttachment } from '../../../services/fileApi';
import TemplatePreviewModal from './TemplatePreviewModal';

async function validateUploadFiles(files, q) {
  const up = q?.uploadPolicy || {};
  const maxPerMb = Math.max(1, Number(up.maxFileSizeMB ?? 10));
  const maxBytes = maxPerMb * 1024 * 1024;
  const maxDurationSecs = up.maxDurationSeconds ?? (up.maxDurationMinutes ? Math.round(Number(up.maxDurationMinutes) * 60) : null);
  const maxFiles = Math.max(1, Number(up.maxFiles ?? 1));

  if (Array.isArray(files) && files.length > maxFiles) {
    return `Maximum ${maxFiles} file(s) allowed. You selected ${files.length}.`;
  }

  for (const file of files || []) {
    if (file instanceof File || file instanceof Blob) {
      if (file.size > maxBytes) {
        return `File "${file.name || 'selected'}" (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds the maximum allowed size of ${maxPerMb} MB.`;
      }
      const family = getRenderFamily(q?.type);
      const isVideo = String(q?.type || '').toUpperCase() === 'VIDEO' || family === 'video';
      const isAudio = String(q?.type || '').toUpperCase() === 'AUDIO' || family === 'audio';
      if (isVideo) {
        const isMp4 = file.type === 'video/mp4' || /\.mp4$/i.test(file.name || '');
        if (!isMp4) {
          return `Video file "${file.name || 'selected'}" must be in MP4 format (.mp4).`;
        }
      }
      if (isAudio) {
        const isMp3 = file.type === 'audio/mpeg' || file.type === 'audio/mp3' || /\.mp3$/i.test(file.name || '');
        if (!isMp3) {
          return `Audio file "${file.name || 'selected'}" must be in MP3 format (.mp3).`;
        }
      }
      if (isVideo && maxDurationSecs && (file.type?.startsWith('video/') || /\.mp4$/i.test(file.name || ''))) {
        const durationError = await new Promise((resolve) => {
          const video = document.createElement('video');
          video.preload = 'metadata';
          const url = URL.createObjectURL(file);
          video.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            const dur = video.duration;
            file.duration = dur;
            if (Number.isFinite(dur) && dur > maxDurationSecs) {
              const mins = (maxDurationSecs / 60).toFixed(1).replace(/\.0$/, '');
              const actualMins = (dur / 60).toFixed(1).replace(/\.0$/, '');
              resolve(`Video "${file.name}" length is ${actualMins} min (${Math.round(dur)}s), which exceeds the maximum allowed duration of ${mins} min (${maxDurationSecs}s).`);
            } else {
              resolve(null);
            }
          };
          video.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
          };
          video.src = url;
        });
        if (durationError) return durationError;
      }
    }
  }
  return null;
}

function perQuestionUploadHint(q) {
  const up = q?.uploadPolicy || {};
    const maxFiles = Math.max(1, Number(up.maxFiles ?? 1));
  const maxPerMb = Math.max(1, Number(up.maxFileSizeMB ?? 10));
  const hints = [`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} · ${maxPerMb} MB max per file`];
  const qType = String(q?.type || '').toUpperCase();
  if (qType === 'VIDEO') {
    hints.push('Allowed format: MP4 (.mp4)');
  }
  if (qType === 'AUDIO') {
    hints.push('Allowed format: MP3 (.mp3)');
  }
  const maxDurationSecs = up.maxDurationSeconds ?? (up.maxDurationMinutes ? Math.round(Number(up.maxDurationMinutes) * 60) : null);
  if (String(q?.type || '').toUpperCase() === 'VIDEO' && maxDurationSecs) {
    const mins = (maxDurationSecs / 60).toFixed(1).replace(/\.0$/, '');
    hints.push(`Maximum video duration: ${mins} minute${Number(mins) === 1 ? '' : 's'} (${maxDurationSecs}s)`);
  }
  return hints;
}

function normOptions(q) {
  const raw = Array.isArray(q?.options) ? q.options : [];
  return raw.map((o) => (typeof o === 'object' ? (o.label ?? o.value ?? '') : String(o ?? '')));
}

function matrixColumns(q) {
  const cfg = resolveTypeConfig(q);
  if (Array.isArray(cfg.columns) && cfg.columns.length) {
    return cfg.columns.map((c) => (typeof c === 'object' ? (c.label || c.key || '') : String(c)));
  }
  return normOptions(q).length ? normOptions(q) : ['Option 1', 'Option 2', 'Option 3'];
}

function matrixRows(q) {
  const cfg = resolveTypeConfig(q);
  if (Array.isArray(cfg.rows) && cfg.rows.length) return cfg.rows.map(String);
  const opts = normOptions(q);
  return opts.length ? opts : ['Row 1', 'Row 2', 'Row 3'];
}

function likertLabels(q) {
  const cfg = resolveTypeConfig(q);
  if (Array.isArray(cfg.scaleLabels) && cfg.scaleLabels.length) return cfg.scaleLabels;
  return ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
}

function sideBySideColumns(q) {
  return dataGridColumns(q).slice(0, 6);
}

function sideBySideSharedRows(q) {
  const opts = normOptions(q);
  return opts.length ? opts : ['Option 1', 'Option 2', 'Option 3'];
}

function sideBySideColumnOnlyRows(q, colKey) {
  const cfg = resolveTypeConfig(q);
  const map = cfg.columnOnlyOptions && typeof cfg.columnOnlyOptions === 'object' ? cfg.columnOnlyOptions : {};
  const extra = map[colKey];
  return Array.isArray(extra) ? extra.map(String).filter(Boolean) : [];
}

function sideBySideRowsForColumn(q, colKey) {
  return [...sideBySideSharedRows(q), ...sideBySideColumnOnlyRows(q, colKey)];
}

function sideBySideMaxRowCount(q, cols) {
  const sharedLen = sideBySideSharedRows(q).length;
  const colLens = cols.map((c) => sideBySideRowsForColumn(q, c.key).length);
  return Math.max(sharedLen, ...colLens, 1);
}

function isFileFamily(family, type) {
  return family === 'file' || family === 'image' || family === 'video' || family === 'audio' || family === 'camera'
    || ['FILE', 'IMAGE', 'VIDEO', 'AUDIO', 'FILE_MULTI', 'IMAGE_GALLERY', 'SIGNATURE_UPLOAD', 'CAMERA_CAPTURE'].includes(type);
}

export function normalizeTextWithFileValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      text: String(value.text ?? ''),
      files: Array.isArray(value.files) ? value.files : [],
    };
  }
  return { text: typeof value === 'string' ? value : '', files: [] };
}

function isTextWithFileValueEmpty(value) {
  const v = normalizeTextWithFileValue(value);
  const hasText = !!String(v.text || '').trim();
  const hasFiles = v.files.some((f) => f != null && f !== '');
  return !hasText || !hasFiles;
}

function fileAccept(q, family, type) {
  if (type === 'SIGNATURE_UPLOAD' || family === 'signature-upload') return 'image/*';
  if (type === 'IMAGE' || type === 'IMAGE_GALLERY' || family === 'image') return 'image/*';
  if (type === 'VIDEO' || family === 'video') return 'video/mp4,.mp4';
  if (type === 'AUDIO' || family === 'audio') return 'audio/mpeg,audio/mp3,.mp3';
  if (family === 'camera') return 'image/*';
  return undefined;
}

function WordCountFooter({ question, text }) {
  const { minWords, maxWords } = getWordCountLimits(question);
  if (minWords == null && maxWords == null) return null;
  const count = countWords(text);
  const err = wordCountError(question, text);
  const limitHint = [
    minWords != null ? `min ${minWords}` : null,
    maxWords != null ? `max ${maxWords}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <p className={`mt-1 text-xs ${err ? 'text-red-600' : 'text-gray-500'}`}>
      {count} word{count === 1 ? '' : 's'}
      {limitHint ? ` (${limitHint})` : ''}
      {err ? ` — ${err}` : ''}
    </p>
  );
}

function OtherTextInput({ question, value, onChange, readOnly, className }) {
  const cfg = resolveTypeConfig(question);
  const placeholder = String(cfg.otherPlaceholder || 'Please specify…');
  return (
    <input
      type="text"
      value={value || ''}
      readOnly={readOnly}
      disabled={readOnly}
      placeholder={readOnly ? '' : placeholder}
      onChange={(e) => !readOnly && onChange?.(e.target.value)}
      className={className}
    />
  );
}

function formatFileSize(bytes) {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function DownloadableTemplateCard({ question, readOnly = false }) {
  const file = question?.downloadableFile
    || question?.templateFile
    || question?.typeConfig?.downloadableFile
    || question?.typeConfig?.templateFile
    || (question?.downloadUrl ? { url: question.downloadUrl, fileName: 'Download Template (.pdf)' } : null);

  if (!file || (!file.url && !file.fileId && !file.fileName)) return null;

  const fileName = file.fileName || file.name || file.originalName || 'Download Template (.pdf)';
  const fileUrl = file.url || (file.fileId ? `/api/files/download/${file.fileId}` : '');
  const fileId = file.fileId || (() => {
    const m = String(fileUrl).match(/\/download\/([a-zA-Z0-9_-]+)/);
    return m ? m[1] : '';
  })();
  const description = file.description || 'Download this official document template, fill it out, and upload the completed file below.';
  const sizeFormatted = formatFileSize(file.fileSize || file.size);
  const isPdf = /\.pdf$/i.test(fileName) || /\.pdf$/i.test(fileUrl);
  const isDocx = /\.docx$/i.test(fileName) || /\.docx$/i.test(fileUrl);
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setDownloading(true);
      if (fileId) {
        const mime = isDocx
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : isPdf
          ? 'application/pdf'
          : 'application/octet-stream';
        await downloadFileAttachment(fileId, fileName, mime);
      } else if (fileUrl) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast('Official template file will be made available shortly.', { icon: 'ℹ️' });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download template file');
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenPreview = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewOpen(true);
  };

  const hasDownloadSource = !!(fileUrl || fileId);

  return (
    <>
      <div className="my-2.5 rounded-lg border border-indigo-200/90 bg-gradient-to-r from-indigo-50/80 via-blue-50/40 to-white p-3 shadow-xs transition-all hover:border-indigo-300">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 shadow-2xs">
            {isDocx ? (
              <span className="text-lg">📝</span>
            ) : isPdf ? (
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-semibold text-gray-900 break-words" title={fileName}>
                {fileName}
              </span>
              <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-indigo-100/90 text-indigo-800 shrink-0">
                Official Template
              </span>
              {sizeFormatted && <span className="text-[11px] text-gray-500 font-medium shrink-0">({sizeFormatted})</span>}
            </div>
            <p className="text-[11px] text-gray-600 mt-1 leading-snug break-words">
              {description}
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {hasDownloadSource && (
                <button
                  type="button"
                  onClick={handleOpenPreview}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-2xs cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View
                </button>
              )}
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors shadow-xs cursor-pointer"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {downloading ? 'Downloading…' : 'Download Template'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {previewOpen && (
        <TemplatePreviewModal
          file={{ ...file, fileId, url: fileUrl, fileName }}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

/** Detect file media type from mimeType, fileName, or File object. */
function detectFileMediaType(file) {
  const mime = String(file?.mimeType || file?.contentType || (file instanceof File ? file.type : '') || '').toLowerCase();
  const name = String(file?.fileName || file?.originalName || file?.name || '').toLowerCase();
  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/.test(name)) return 'image';
  if (mime.startsWith('video/') || /\.(mp4|mov|webm|mkv|avi|ogv)$/.test(name)) return 'video';
  return 'file';
}

/**
 * A single rich-media file entry.
 * – For local File objects (during edit): uses URL.createObjectURL.
 * – For stored files (fileId): fetches a blob via the file-service on mount.
 */
function RichFileEntry({ file, fi, onRemove, readOnly }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // 'view' | 'download' | null
  const blobUrlRef = useRef(null);

  const name = file?.fileName || file?.originalName || file?.name || 'File';
  const fileId = String(file?.fileId || file?.id || '').trim();
  const externalUrl = file?.url || file?.presignedUrl || file?.downloadUrl;
  const mimeType = file?.mimeType || file?.contentType || (file instanceof File ? file.type : 'application/octet-stream');
  const mediaType = detectFileMediaType(file);
  const size = file?.fileSize || file?.size;
  const sizeFormatted = Number.isFinite(size) && size > 0
    ? `${size >= 1024 * 1024 ? (size / (1024 * 1024)).toFixed(2) + ' MB' : (size / 1024).toFixed(1) + ' KB'}`
    : null;

  // For local File objects, create object URL immediately
  useEffect(() => {
    if (file instanceof File && (mediaType === 'image' || mediaType === 'video')) {
      const url = URL.createObjectURL(file);
      setBlobUrl(url);
      blobUrlRef.current = url;
      return () => {
        URL.revokeObjectURL(url);
        blobUrlRef.current = null;
      };
    }
  }, [file, mediaType]);

  // For stored files with a fileId or externalUrl, prepare preview
  useEffect(() => {
    if (file instanceof File || (mediaType !== 'image' && mediaType !== 'video')) return;
    if (externalUrl && !fileId) {
      setBlobUrl(externalUrl);
      setLoadingPreview(false);
      return;
    }
    if (!fileId) return;

    let cancelled = false;
    setLoadingPreview(true);
    (async () => {
      try {
        // Try fetching presigned URL first (fast, streams native range requests without downloading whole buffer into RAM)
        const { getPresignedUrl: gpu, extractPresignedUrlFromResponse: exp } = await import('../../../services/fileApi');
        try {
          const presignRes = await gpu(fileId, 3600);
          const pUrl = exp(presignRes);
          if (pUrl && !cancelled) {
            setBlobUrl(pUrl);
            setLoadingPreview(false);
            return;
          }
        } catch (_) { /* fallback to blob download for small assets */ }

        // For large media like video, do not download hundreds of megabytes into client RAM
        if (mediaType === 'video') {
          if (!cancelled) setLoadingPreview(false);
          return;
        }

        const { downloadFile: dl } = await import('../../../services/fileApi');
        const res = await dl(fileId);
        if (cancelled) return;
        const blob = res.data;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      } catch {
        // preview not available – fall back to action buttons only
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [fileId, mediaType, externalUrl]);

  const handleOpenNewTab = async () => {
    if (busyAction) return;
    setBusyAction('view');
    try {
      if (fileId && !(file instanceof File)) {
        // Stored files: always ask for a fresh link. A URL kept in state/data can be an expired presigned link.
        const { getPresignedUrl: gpu, extractPresignedUrlFromResponse: exp } = await import('../../../services/fileApi');
        let fresh = '';
        try {
          fresh = exp(await gpu(fileId, 3600)) || '';
        } catch (_) { /* fall through to authenticated download */ }
        if (fresh) window.open(fresh, '_blank', 'noopener,noreferrer');
        else await openFileInNewTab(fileId, { mimeType });
      } else if (externalUrl) {
        window.open(externalUrl, '_blank', 'noopener,noreferrer');
      } else if (blobUrl && typeof blobUrl === 'string' && blobUrl.startsWith('http')) {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      } else if (blobUrl) {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      } else if (fileId) {
        await openFileInNewTab(fileId, { mimeType });
      }
    } catch (err) {
      if (!fileId && externalUrl) window.open(externalUrl, '_blank', 'noopener,noreferrer');
      else toast.error(err?.response?.data?.message || 'Could not open file');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownload = async () => {
    if (busyAction) return;
    setBusyAction('download');
    try {
      if (fileId && !(file instanceof File)) {
        await downloadFileAttachment(fileId, name, mimeType);
      } else if (blobUrl || externalUrl) {
        // For local blobs or direct URLs, trigger download via anchor
        const a = document.createElement('a');
        a.href = blobUrl || externalUrl;
        a.download = name;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (fileId) {
        await downloadFileAttachment(fileId, name, mimeType);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download file');
    } finally {
      setBusyAction(null);
    }
  };

  const canOpen = Boolean(fileId || externalUrl || blobUrl);
  const canDownload = Boolean(fileId || externalUrl || blobUrl);

  // ── Image preview ──────────────────────────────────────────────────────────
  if (mediaType === 'image') {
    return (
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {/* Preview area */}
        <div className="relative bg-gray-50 border-b border-gray-100" style={{ minHeight: 120, maxHeight: 220 }}>
          {loadingPreview && !blobUrl && (
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="animate-spin w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          )}
          {blobUrl ? (
            <img
              src={blobUrl}
              alt={name}
              className="w-full h-full object-contain"
              style={{ maxHeight: 220 }}
            />
          ) : !loadingPreview ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-300">
              <svg className="w-10 h-10 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-xs">Preview unavailable</span>
            </div>
          ) : null}
        </div>
        {/* File info + actions */}
        <div className="px-3 py-2 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-800 truncate" title={name}>{name}</p>
            {sizeFormatted && <p className="text-[11px] text-gray-400">{sizeFormatted}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canOpen && (
              <button type="button" onClick={handleOpenNewTab} disabled={!!busyAction}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-indigo-600 border border-indigo-200 hover:bg-indigo-50 disabled:opacity-50 transition-colors">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {busyAction === 'view' ? 'Opening…' : 'View in new tab'}
              </button>
            )}
            {canDownload && (
              <button type="button" onClick={handleDownload} disabled={!!busyAction}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {busyAction === 'download' ? 'Downloading…' : 'Download'}
              </button>
            )}
            {!readOnly && onRemove && (
              <button type="button" onClick={() => onRemove(fi)}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
                title="Remove file">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Video player ───────────────────────────────────────────────────────────
  if (mediaType === 'video') {
    return (
      <div className="rounded-lg border border-gray-200 bg-black overflow-hidden shadow-sm">
        {/* Player */}
        <div className="relative bg-black" style={{ minHeight: 160 }}>
          {loadingPreview && !blobUrl && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <svg className="animate-spin w-6 h-6 text-white/60 mb-1" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <span className="text-xs text-white/50">Loading video…</span>
            </div>
          )}
          {blobUrl ? (
            <video
              src={blobUrl}
              controls
              controlsList="nodownload"
              playsInline
              preload="metadata"
              className="w-full block"
              style={{ maxHeight: 320 }}
            />
          ) : !loadingPreview ? (
            <div className="flex flex-col items-center justify-center py-10 text-white/40">
              <svg className="w-10 h-10 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-xs">Video preview unavailable</span>
            </div>
          ) : null}
        </div>
        {/* File info + actions */}
        <div className="px-3 py-2 bg-gray-900 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-200 truncate" title={name}>{name}</p>
            {sizeFormatted && <p className="text-[11px] text-gray-500">{sizeFormatted}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canOpen && (
              <button type="button" onClick={handleOpenNewTab} disabled={!!busyAction}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-indigo-300 border border-indigo-700 hover:bg-indigo-900/50 disabled:opacity-50 transition-colors">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {busyAction === 'view' ? 'Opening…' : 'Open in new tab'}
              </button>
            )}
            {canDownload && (
              <button type="button" onClick={handleDownload} disabled={!!busyAction}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-gray-300 border border-gray-700 hover:bg-gray-800 disabled:opacity-50 transition-colors">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {busyAction === 'download' ? 'Downloading…' : 'Download'}
              </button>
            )}
            {!readOnly && onRemove && (
              <button type="button" onClick={() => onRemove(fi)}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-red-400 border border-red-800 hover:bg-red-900/40 transition-colors"
                title="Remove file">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Generic file entry (non-image, non-video) ──────────────────────────────
  const isBusy = !!busyAction;
  return (
    <div className="flex items-center justify-between gap-2 rounded bg-white border border-gray-200 px-3 py-1.5 text-xs text-gray-800 shadow-sm">
      <div className="flex items-center gap-2 min-w-0">
        <svg className="w-4 h-4 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="truncate font-medium text-gray-800" title={name}>{name}</span>
        {sizeFormatted && <span className="text-gray-400 shrink-0">({sizeFormatted})</span>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canOpen && (
          <button type="button" onClick={handleOpenNewTab} disabled={isBusy}
            className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline disabled:opacity-50">
            {busyAction === 'view' ? 'Opening…' : 'View'}
          </button>
        )}
        {canDownload && (
          <button type="button" onClick={handleDownload} disabled={isBusy}
            className="font-medium text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50">
            {busyAction === 'download' ? 'Downloading…' : 'Download'}
          </button>
        )}
        {!readOnly && onRemove && (
          <button type="button" onClick={() => onRemove(fi)}
            className="font-medium text-red-500 hover:text-red-700 hover:underline ml-1" title="Remove file">
            ✕ Remove
          </button>
        )}
      </div>
    </div>
  );
}

function FileValueDisplay({ value, deliveryMode = 'open', onRemove = null, readOnly = true }) {
  const files = Array.isArray(value) ? value : (value ? [value] : []);
  if (!files.length) return <span className="text-gray-400">No files uploaded</span>;

  return (
    <div className="space-y-2 w-full">
      {files.map((file, fi) => (
        <RichFileEntry
          key={fi}
          file={file}
          fi={fi}
          onRemove={onRemove}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}

function ContentBlock({ question, family }) {
  const label = question?.label || '';
  if (family === 'section-header') {
    return <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-3">{label}</h3>;
  }
  if (family === 'divider') {
    return <hr className="my-4 border-gray-200" />;
  }
  if (family === 'terms-link') {
    const url = question?.placeholder || question?.typeConfig?.url || '#';
    return (
      <p className="text-sm text-gray-700 mb-3">
        <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline font-medium">
          {label || 'View terms'}
        </a>
      </p>
    );
  }
  if (family === 'embed') {
    const url = question?.placeholder || question?.typeConfig?.embedUrl || '';
    return (
      <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        {url ? (
          <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">{url}</a>
        ) : (
          <span className="text-gray-400 italic">Video URL not configured</span>
        )}
      </div>
    );
  }
  if (family === 'image-display') {
    const url = question?.placeholder || question?.typeConfig?.imageUrl || '';
    return url ? (
      <img src={url} alt={label || 'Image'} className="mb-3 max-w-full rounded border border-gray-200" />
    ) : (
      <div className="mb-3 rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-xs text-gray-400">Image not configured</div>
    );
  }
  if (family === 'callout') {
    return (
      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 whitespace-pre-wrap">{label}</div>
    );
  }
  if (family === 'fixed-table') {
    return (
      <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 whitespace-pre-wrap">{label}</div>
    );
  }
  return (
    <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
      {label}
    </div>
  );
}

/** Returns true when a required question has no answer */
export function isQuestionValueEmpty(question, value) {
  const family = getRenderFamily(question?.type);
  const type = String(question?.type || '').toUpperCase();

  if (isStaticContentQuestion(question) || isPurePrintDeclaration(question)) return false;
  if (isDeclarationQuestion(question)) {
    return !!validateDeclarationValue(question, value);
  }
  if (type === 'PIPED_DISPLAY' || family === 'piped-display') return false;
  if (type === 'SINGLE_CHOICE_OTHER' || type === 'DROPDOWN_OTHER' || family === 'choice-single-other' || family === 'dropdown-other') {
    const v = normalizeSingleChoiceOtherValue(value);
    if (!String(v.selection || '').trim()) return true;
    if (isOtherOptionValue(v.selection, question) && !String(v.otherText || '').trim()) return true;
    return false;
  }
  if (type === 'MULTIPLE_CHOICE_OTHER' || family === 'choice-multi-other') {
    const v = normalizeMultipleChoiceOtherValue(value);
    if (!v.selections.length) return true;
    if (v.selections.some((s) => isOtherOptionValue(s, question)) && !String(v.otherText || '').trim()) return true;
    const min = Number(question?.validationRules?.minSelections);
    if (Number.isFinite(min) && min > 0 && v.selections.length < min) return true;
    const max = Number(question?.validationRules?.maxSelections);
    if (Number.isFinite(max) && max > 0 && v.selections.length > max) return true;
    return false;
  }
  if (family === 'choice-multi' || type === 'MULTIPLE_CHOICE' || type === 'MATRIX_MULTI') {
    const arr = Array.isArray(value) ? value : [];
    if (!arr.length) return true;
    if (type === 'MULTIPLE_CHOICE') {
      const min = Number(question?.validationRules?.minSelections);
      if (Number.isFinite(min) && min > 0 && arr.length < min) return true;
      const max = Number(question?.validationRules?.maxSelections);
      if (Number.isFinite(max) && max > 0 && arr.length > max) return true;
    }
    return false;
  }
  if (['labeled-list', 'table-rows'].includes(family) || type === 'LABELED_LIST' || type === 'TABLE_ROWS') {
    const complex = isComplexAnswerEmpty(question, value);
    if (complex != null) return complex;
    return !Array.isArray(value) || !value.some((r) => String(r ?? '').trim());
  }
  if (family === 'data-grid' || type === 'DATA_GRID' || type === 'COMPARISON_TABLE' || type === 'DROPDOWN_GRID' || type === 'DYNAMIC_TABLE') {
    return isDataGridAnswerEmpty(value, question);
  }
  if (family === 'repeating' || type === 'REPEATING_SECTION') {
    const complex = isComplexAnswerEmpty(question, value);
    if (complex != null) return complex;
    return !Array.isArray(value) || !value.length;
  }
  if (family === 'matrix-single' || family === 'matrix-multi' || type === 'MATRIX_SINGLE' || type === 'MATRIX_MULTI') {
    const complex = isComplexAnswerEmpty(question, value);
    if (complex != null) return complex;
  }
  if (family === 'side-by-side-choice' || type === 'SIDE_BY_SIDE_CHOICE') {
    const complex = isComplexAnswerEmpty(question, value);
    if (complex != null) return complex;
  }
  if (family === 'ranking') {
    return !Array.isArray(value) || value.length < normOptions(question).filter(Boolean).length;
  }
  if (family === 'rating' || family === 'nps' || family === 'likert' || family === 'scale-buttons' || family === 'slider') {
    return value == null || value === '';
  }
  if (family === 'date-range') {
    return !value?.start || !value?.end;
  }
  if (type === 'TEXT_WITH_FILE') {
    const v = normalizeTextWithFileValue(value);
    if (isTextWithFileValueEmpty(value)) return true;
    if (wordCountError(question, v.text)) return true;
    return false;
  }
  if (family === 'textarea' || type === 'TEXTAREA') {
    if (value == null || String(value).trim() === '') return true;
    if (wordCountError(question, value)) return true;
    return false;
  }
  if (family === 'text' || type === 'TEXT') {
    if (value == null || String(value).trim() === '') return true;
    if (wordCountError(question, value)) return true;
    return false;
  }
  if (family === 'percentage' || type === 'PERCENTAGE') {
    return value == null || String(value).trim() === '';
  }
  if (isFileFamily(family, type)) {
    const files = Array.isArray(value) ? value : (value ? [value] : []);
    return !files.length;
  }
  if (type === 'CHECKBOX_SINGLE') return !value;
  if (Array.isArray(value)) return !value.length;
  return value == null || String(value).trim() === '';
}

/**
 * Unified question field renderer for public forms, previews, and read-only views.
 */
export default function ApplicationQuestionField({
  question: q,
  value,
  onChange,
  keyPrefix = '',
  hasError = false,
  readOnly = false,
  preview = false,
  showLabel = true,
  showTypeBadge = false,
  answersContext = {},
  attachmentDeliveryMode = 'open',
}) {
  const [fileValidationErr, setFileValidationErr] = useState(null);
  const fileInputRef = useRef(null);
  const textFileInputRef = useRef(null);
  const ro = readOnly || preview;
  const disabledCls = ro ? ' bg-gray-50 text-gray-700 cursor-not-allowed' : '';
  const family = getRenderFamily(q?.type);
  const type = String(q?.type || '').toUpperCase();
  const required = Boolean(q?.validationRules?.required ?? q?.required);
  const errCls = hasError ? 'border-red-500 bg-red-50' : 'border-gray-300';
  const inputCls = `w-full rounded border px-3 py-2 text-sm ${errCls}${disabledCls}`;

  if (family === 'hidden') return null;

  const labelEl = showLabel ? (
    <label className="block text-sm font-medium text-gray-900 mb-1">
      {q.label}
      {!ro && required && <span className="text-red-500 ml-1">*</span>}
    </label>
  ) : null;

  const pctLimits = family === 'percentage' ? getPercentageLimits(q) : null;
  const pctRangeErr = !ro && family === 'percentage' ? percentageRangeError(q, value) : null;

  const errorEl = !ro && (hasError || pctRangeErr) ? (
    <p className="mt-1 text-xs text-red-600">
      {pctRangeErr
        || (family === 'rating' ? 'Please select a rating' : 'This field is required')}
    </p>
  ) : null;

  if (family === 'piped-display' || type === 'PIPED_DISPLAY') {
    const cfg = resolveTypeConfig(q);
    const piped = resolvePipedSourceValue(cfg.sourceFieldKey, answersContext);
    const emptyMessage = String(cfg.emptyMessage || '—');
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap">
          {piped || emptyMessage}
        </div>
      </div>
    );
  }

  if (family === 'section-header' || family === 'divider'
    || family === 'terms-link' || family === 'embed' || family === 'image-display'
    || family === 'fixed-table' || (isStaticContentQuestion(q) && !isDeclarationQuestion(q))) {
    return <div key={`${keyPrefix}${q.id}`}><ContentBlock question={q} family={family} /></div>;
  }

  if (isDeclarationQuestion(q)) {
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {showTypeBadge && null}
        <DeclarationBlock question={q} value={value} onChange={onChange} readOnly={ro} hasError={hasError} />
        {!ro && hasError && (
          <p className="mt-1 text-xs text-red-600">
            {validateDeclarationValue(q, value) || 'Please complete all required declaration fields'}
          </p>
        )}
      </div>
    );
  }

  if (family === 'signature-pad' || type === 'WITNESS_SIGNATURE') {
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {showLabel && (
          <label className="block text-sm font-medium text-gray-900 mb-1">
            {q.label}
            {!ro && required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <SignaturePadPreview question={q} readOnly={ro} />
      </div>
    );
  }

  // —— Text-like inputs ——
  if (['text', 'email', 'url', 'password', 'number', 'currency', 'year', 'percentage', 'hidden'].includes(family)) {
    const htmlType = family === 'email' ? 'email'
      : family === 'url' ? 'url'
        : family === 'password' ? 'password'
          : family === 'number' || family === 'currency' ? 'number'
            : family === 'year' ? 'number'
              : 'text';
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        {family === 'percentage' ? (
          ro ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
              {formatDisplayValue(value, q) || '—'}
            </div>
          ) : (
            <div className="relative">
              <input
                type="number"
                min={0}
                max={pctLimits?.max != null ? pctLimits.max : undefined}
                step="0.01"
                value={value || ''}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder="0"
                className={`${inputCls} pr-8`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">%</span>
            </div>
          )
        ) : ro && family === 'text' ? (
          // Short-text answers can be long (no maxLength); single-line inputs hide overflow when read-only.
          <div className={`${inputCls} whitespace-pre-wrap break-words min-h-[2.5rem]`}>
            {String(value || '').trim() ? String(value) : '—'}
          </div>
        ) : (
          <input
            type={htmlType}
            value={value || ''}
            onChange={(e) => !ro && onChange?.(e.target.value)}
            readOnly={ro}
            disabled={ro}
            min={family === 'year' ? (q?.validationRules?.min ?? 1900) : undefined}
            max={family === 'year' ? (q?.validationRules?.max ?? new Date().getFullYear()) : undefined}
            placeholder={ro ? '' : (q.placeholder || '')}
            maxLength={!ro && family === 'text' && Number.isFinite(Number(q?.validationRules?.maxLength)) ? Number(q.validationRules.maxLength) : undefined}
            className={inputCls}
          />
        )}
        {!ro && family === 'percentage' && (
          <p className="mt-1 text-[11px] text-gray-500">
            {pctLimits?.max != null ? `Allowed range: 0 – ${pctLimits.max}%` : 'Allowed range: 0% and above (no upper limit)'}
          </p>
        )}
        {!ro && <WordCountFooter question={q} text={value || ''} />}
        {errorEl}
      </div>
    );
  }

  if (family === 'phone') {
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <PhoneInput value={value || ''} onChange={({ target: { value: v } }) => !ro && onChange?.(v)} disabled={ro}
          error={hasError ? (q?.validationRules?.errorMessage || '') : ''} placeholder={q.placeholder || 'Enter phone number'} />
        {errorEl}
      </div>
    );
  }

  if (family === 'textarea') {
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <textarea value={value || ''} onChange={(e) => !ro && onChange?.(e.target.value)} readOnly={ro} disabled={ro} rows={3} className={inputCls} />
        {!ro && <WordCountFooter question={q} text={value || ''} />}
        {errorEl}
      </div>
    );
  }

  // —— Date/time ——
  if (['date', 'time', 'datetime', 'month', 'week'].includes(family)) {
    const htmlType = family === 'datetime' ? 'datetime-local' : family;
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <input type={htmlType} value={value || ''} onChange={(e) => !ro && onChange?.(e.target.value)} readOnly={ro} disabled={ro} className={inputCls} />
        {errorEl}
      </div>
    );
  }

  if (family === 'date-range') {
    const range = value && typeof value === 'object' ? value : {};
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input type="date" value={range.start || ''} onChange={(e) => !ro && onChange?.({ ...range, start: e.target.value })}
            readOnly={ro} disabled={ro} className={inputCls} placeholder="Start" />
          <input type="date" value={range.end || ''} onChange={(e) => !ro && onChange?.({ ...range, end: e.target.value })}
            readOnly={ro} disabled={ro} className={inputCls} placeholder="End" />
        </div>
        {errorEl}
      </div>
    );
  }

  // —— Choices ——
  if (family === 'dropdown-other' || type === 'DROPDOWN_OTHER') {
    const opts = normOptions(q);
    const otherLabel = getOtherOptionLabel(q);
    const v = normalizeSingleChoiceOtherValue(value);
    const showOther = isOtherOptionValue(v.selection, q);
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <select
          value={v.selection || ''}
          onChange={(e) => {
            if (ro) return;
            const selection = e.target.value;
            onChange?.({ selection, otherText: isOtherOptionValue(selection, q) ? v.otherText : '' });
          }}
          disabled={ro}
          className={inputCls}
        >
          <option value="">{ro ? '—' : 'Select...'}</option>
          {opts.map((o, oi) => <option key={`${keyPrefix}${q.id}_${oi}`} value={o}>{o}</option>)}
          {!opts.some((o) => o.toLowerCase() === otherLabel.toLowerCase()) && (
            <option value={otherLabel}>{otherLabel}</option>
          )}
        </select>
        {showOther && (
          <div className="mt-2">
            <OtherTextInput
              question={q}
              value={v.otherText}
              readOnly={ro}
              className={inputCls}
              onChange={(otherText) => onChange?.({ ...v, otherText })}
            />
          </div>
        )}
        {errorEl}
      </div>
    );
  }

  if (family === 'dropdown') {
    const opts = normOptions(q);
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <select value={value || ''} onChange={(e) => !ro && onChange?.(e.target.value)} disabled={ro} className={inputCls}>
          <option value="">{ro ? '—' : 'Select...'}</option>
          {opts.map((o, oi) => <option key={`${keyPrefix}${q.id}_${oi}`} value={o}>{o}</option>)}
        </select>
        {errorEl}
      </div>
    );
  }

  if (family === 'choice-single-other' || type === 'SINGLE_CHOICE_OTHER') {
    const opts = normOptions(q);
    const otherLabel = getOtherOptionLabel(q);
    const allOpts = opts.some((o) => o.toLowerCase() === otherLabel.toLowerCase()) ? opts : [...opts, otherLabel];
    const v = normalizeSingleChoiceOtherValue(value);
    const showOther = isOtherOptionValue(v.selection, q);
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`space-y-1 ${hasError ? 'rounded border border-red-400 bg-red-50 p-2' : ''}`}>
          {allOpts.map((o, oi) => (
            <label key={`${keyPrefix}${q.id}_${oi}`} className={`flex items-center gap-2 text-sm ${ro ? 'cursor-default' : ''}`}>
              <input
                type="radio"
                name={`${keyPrefix}${q.id}`}
                checked={v.selection === o}
                onChange={() => !ro && onChange?.({ selection: o, otherText: isOtherOptionValue(o, q) ? v.otherText : '' })}
                disabled={ro}
              />
              {o}
            </label>
          ))}
          {showOther && (
            <OtherTextInput
              question={q}
              value={v.otherText}
              readOnly={ro}
              className={`${inputCls} mt-1`}
              onChange={(otherText) => onChange?.({ ...v, otherText })}
            />
          )}
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'choice-single' && type !== 'BUTTON_GROUP') {
    const opts = normOptions(q);
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`space-y-1 ${hasError ? 'rounded border border-red-400 bg-red-50 p-2' : ''}`}>
          {opts.map((o, oi) => (
            <label key={`${keyPrefix}${q.id}_${oi}`} className={`flex items-center gap-2 text-sm ${ro ? 'cursor-default' : ''}`}>
              <input type="radio" name={`${keyPrefix}${q.id}`} checked={value === o} onChange={() => !ro && onChange?.(o)} disabled={ro} />
              {o}
            </label>
          ))}
        </div>
        {errorEl}
      </div>
    );
  }

  if (type === 'BUTTON_GROUP') {
    const opts = normOptions(q);
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="flex flex-wrap gap-2">
          {opts.map((o, oi) => (
            <button key={oi} type="button" disabled={ro}
              onClick={() => !ro && onChange?.(o)}
              className={`px-3 py-1.5 rounded-md text-sm border ${value === o ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}>
              {o}
            </button>
          ))}
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'choice-multi-other' || type === 'MULTIPLE_CHOICE_OTHER') {
    const opts = normOptions(q);
    const otherLabel = getOtherOptionLabel(q);
    const allOpts = opts.some((o) => o.toLowerCase() === otherLabel.toLowerCase()) ? opts : [...opts, otherLabel];
    const v = normalizeMultipleChoiceOtherValue(value);
    const showOther = v.selections.some((s) => isOtherOptionValue(s, q));
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`space-y-1 ${hasError ? 'rounded border border-red-400 bg-red-50 p-2' : ''}`}>
          {allOpts.map((o, oi) => (
            <label key={`${keyPrefix}${q.id}_${oi}`} className={`flex items-center text-sm ${ro ? 'cursor-default' : ''}`}>
              <input
                type="checkbox"
                checked={v.selections.includes(o)}
                disabled={ro}
                onChange={(e) => {
                  if (ro) return;
                  const nextSelections = e.target.checked
                    ? [...v.selections, o]
                    : v.selections.filter((x) => x !== o);
                  onChange?.({
                    selections: nextSelections,
                    otherText: nextSelections.some((s) => isOtherOptionValue(s, q)) ? v.otherText : '',
                  });
                }}
                className="mr-2"
              />
              {o}
            </label>
          ))}
          {showOther && (
            <OtherTextInput
              question={q}
              value={v.otherText}
              readOnly={ro}
              className={`${inputCls} mt-1`}
              onChange={(otherText) => onChange?.({ ...v, otherText })}
            />
          )}
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'choice-multi' || type === 'MULTIPLE_CHOICE') {
    const opts = normOptions(q);
    const arr = Array.isArray(value) ? value : [];
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`space-y-1 ${hasError ? 'rounded border border-red-400 bg-red-50 p-2' : ''}`}>
          {opts.map((o, oi) => (
            <label key={`${keyPrefix}${q.id}_${oi}`} className={`flex items-center text-sm ${ro ? 'cursor-default' : ''}`}>
              <input type="checkbox" checked={arr.includes(o)} disabled={ro}
                onChange={(e) => {
                  if (ro) return;
                  onChange?.(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o));
                }} className="mr-2" />
              {o}
            </label>
          ))}
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'yes-no' || type === 'YES_NO' || type === 'TRUE_FALSE') {
    const opts = type === 'TRUE_FALSE' ? ['True', 'False'] : ['Yes', 'No'];
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`flex gap-4 ${hasError ? 'rounded border border-red-400 bg-red-50 p-2' : ''}`}>
          {opts.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm">
              <input type="radio" name={`${keyPrefix}${q.id}`} checked={value === opt} onChange={() => !ro && onChange?.(opt)} disabled={ro} />
              {opt}
            </label>
          ))}
        </div>
        {errorEl}
      </div>
    );
  }

  if (type === 'CHECKBOX_SINGLE') {
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        <label className={`flex items-start gap-2 text-sm ${ro ? 'cursor-default' : 'cursor-pointer'}`}>
          <input type="checkbox" checked={!!value} onChange={(e) => !ro && onChange?.(e.target.checked)} disabled={ro} className="mt-0.5" />
          <span>{q.label}{!ro && required && <span className="text-red-500 ml-1">*</span>}</span>
        </label>
        {errorEl}
      </div>
    );
  }

  // —— Scales ——
  if (family === 'rating' || type === 'RATING' || type === 'STAR_RATING_HALF') {
    const cfg = resolveTypeConfig(q);
    const max = q?.validationRules?.max ?? cfg.max ?? 5;
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`flex items-center gap-1 ${hasError ? 'rounded border border-red-400 bg-red-50 p-2' : ''}`}>
          {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
            <button key={star} type="button" disabled={ro}
              onClick={() => !ro && onChange?.(value === star ? null : star)}
              className={`text-2xl leading-none ${ro ? 'cursor-default' : 'cursor-pointer hover:text-yellow-400'} ${Number(value) >= star ? 'text-yellow-400' : 'text-gray-300'}`}>
              ★
            </button>
          ))}
          <span className="ml-2 text-sm text-gray-500">{value ? `${value} / ${max}` : 'Not rated'}</span>
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'slider' || type === 'SLIDER' || type === 'RANGE') {
    const cfg = resolveTypeConfig(q);
    const min = cfg.min ?? q?.validationRules?.min ?? 0;
    const max = cfg.max ?? q?.validationRules?.max ?? 100;
    const step = cfg.step ?? 1;
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <input type="range" min={min} max={max} step={step} value={value ?? min} disabled={ro}
          onChange={(e) => !ro && onChange?.(Number(e.target.value))} className="w-full accent-indigo-600" />
        <div className="text-xs text-gray-500 mt-1">{value ?? min} / {max}</div>
        {errorEl}
      </div>
    );
  }

  if (family === 'nps' || type === 'NPS') {
    const cfg = resolveTypeConfig(q);
    const min = cfg.min ?? 0;
    const max = cfg.max ?? 10;
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="flex flex-wrap gap-1">
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
            <button key={n} type="button" disabled={ro} onClick={() => !ro && onChange?.(n)}
              className={`w-8 h-8 rounded text-xs font-medium border ${Number(value) === n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}>
              {n}
            </button>
          ))}
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'likert' || type === 'LIKERT' || type === 'OPINION_SCALE') {
    const labels = likertLabels(q);
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {labels.map((lbl, i) => (
              <button key={i} type="button" disabled={ro} onClick={() => !ro && onChange?.(i)}
                className={`px-2 py-1.5 rounded text-xs border text-center max-w-[100px] ${Number(value) === i ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'scale-buttons' || type === 'SCALE_1_10') {
    const cfg = resolveTypeConfig(q);
    const min = cfg.min ?? 1;
    const max = cfg.max ?? 10;
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="flex flex-wrap gap-1">
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
            <button key={n} type="button" disabled={ro} onClick={() => !ro && onChange?.(n)}
              className={`w-9 h-9 rounded text-sm font-medium border ${Number(value) === n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}>
              {n}
            </button>
          ))}
        </div>
        {errorEl}
      </div>
    );
  }

  // —— Matrix ——
  if (family === 'matrix-single' || family === 'matrix-multi') {
    const rows = matrixRows(q);
    const cols = matrixColumns(q);
    const val = value && typeof value === 'object' ? value : {};
    const multi = family === 'matrix-multi';
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3 overflow-x-auto">
        {labelEl}
        <table className="w-full border-collapse text-sm border border-gray-300">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-2 py-1 text-left" />
              {cols.map((c, ci) => <th key={ci} className="border border-gray-300 px-2 py-1 text-center font-medium text-gray-700">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                <td className="border border-gray-300 px-2 py-1 font-medium text-gray-800">{row}</td>
                {cols.map((c, ci) => {
                  const cellVal = val[String(ri)] ?? val[ri];
                  const checked = multi ? (Array.isArray(cellVal) ? cellVal.includes(c) : false) : cellVal === c;
                  return (
                    <td key={ci} className="border border-gray-300 px-2 py-1 text-center">
                      <input
                        type={multi ? 'checkbox' : 'radio'}
                        name={multi ? undefined : `${keyPrefix}${q.id}_row_${ri}`}
                        checked={checked}
                        disabled={ro}
                        onChange={() => {
                          if (ro) return;
                          if (multi) {
                            const arr = Array.isArray(cellVal) ? [...cellVal] : [];
                            const next = checked ? arr.filter((x) => x !== c) : [...arr, c];
                            onChange?.({ ...val, [String(ri)]: next });
                          } else {
                            onChange?.({ ...val, [String(ri)]: c });
                          }
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {errorEl}
      </div>
    );
  }

  if (family === 'side-by-side-choice' || type === 'SIDE_BY_SIDE_CHOICE') {
    const cols = sideBySideColumns(q);
    const maxRows = sideBySideMaxRowCount(q, cols);
    const val = value && typeof value === 'object' ? value : {};
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3 overflow-x-auto">
        {labelEl}
        <table className="w-full border-collapse text-sm border border-gray-800">
          <thead>
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className="border border-gray-800 px-3 py-2 text-center font-semibold text-gray-900 bg-white whitespace-pre-wrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxRows }, (_, rowIdx) => (
              <tr key={rowIdx}>
                {cols.map((c) => {
                  const colRows = sideBySideRowsForColumn(q, c.key);
                  const optionLabel = colRows[rowIdx];
                  if (!optionLabel) {
                    return <td key={c.key} className="border border-gray-800 bg-gray-50 px-3 py-2" />;
                  }
                  const checked = val[c.key] === optionLabel;
                  return (
                    <td key={c.key} className="border border-gray-800 px-3 py-2 text-center align-middle">
                      <label className={`inline-flex items-start justify-center gap-2 text-left ${ro ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name={`${keyPrefix}${q.id}_${c.key}`}
                          checked={checked}
                          disabled={ro}
                          onChange={() => {
                            if (ro) return;
                            onChange?.({ ...val, [c.key]: optionLabel });
                          }}
                          className="mt-0.5 shrink-0"
                        />
                        <span className="text-sm text-gray-900 whitespace-pre-wrap">{optionLabel}</span>
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {errorEl}
      </div>
    );
  }

  if (family === 'ranking' || type === 'RANKING') {
    const opts = normOptions(q).filter(Boolean);
    const ranked = Array.isArray(value) ? value : [];
    const move = (from, to) => {
      if (ro || to < 0 || to >= ranked.length) return;
      const next = [...ranked];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      onChange?.(next);
    };
    const addToRank = (opt) => {
      if (ro || ranked.includes(opt)) return;
      onChange?.([...ranked, opt]);
    };
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Available</p>
            {opts.filter((o) => !ranked.includes(o)).map((o) => (
              <button key={o} type="button" disabled={ro} onClick={() => addToRank(o)}
                className="block w-full text-left px-2 py-1 text-sm rounded border border-gray-200 mb-1 hover:bg-gray-50">{o}</button>
            ))}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Ranked</p>
            {ranked.map((o, i) => (
              <div key={o} className="flex items-center gap-1 mb-1">
                <span className="text-xs text-gray-500 w-5">{i + 1}.</span>
                <span className="flex-1 text-sm">{o}</span>
                {!ro && (
                  <>
                    <button type="button" onClick={() => move(i, i - 1)} className="text-xs text-gray-500 px-1">↑</button>
                    <button type="button" onClick={() => move(i, i + 1)} className="text-xs text-gray-500 px-1">↓</button>
                    <button type="button" onClick={() => onChange?.(ranked.filter((x) => x !== o))} className="text-xs text-red-500 px-1">×</button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        {errorEl}
      </div>
    );
  }

  // —— Lists & tables ——
  if (type === 'LABELED_LIST' || family === 'labeled-list') {
    const rows = Array.isArray(q.options) && q.options.length ? q.options : ['', '', ''];
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`space-y-1.5 ${hasError ? 'rounded border border-red-400 bg-red-50 p-2' : ''}`}>
          {rows.map((placeholder, rowIdx) => {
            const rowLabel = String.fromCharCode(97 + rowIdx);
            const rowValue = Array.isArray(value) ? (value[rowIdx] || '') : '';
            return (
              <div key={rowIdx} className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 w-5 shrink-0">{rowLabel}.</span>
                <input type="text" value={rowValue} readOnly={ro} disabled={ro} placeholder={ro ? '' : (placeholder || '')}
                  onChange={(e) => {
                    if (ro) return;
                    const current = Array.isArray(value) ? [...value] : new Array(rows.length).fill('');
                    current[rowIdx] = e.target.value;
                    onChange?.(current);
                  }} className={`flex-1 rounded border border-gray-300 px-3 py-2 text-sm${disabledCls}`} />
              </div>
            );
          })}
        </div>
        {errorEl}
      </div>
    );
  }

  if (type === 'TABLE_ROWS' || family === 'table-rows') {
    const rows = Array.isArray(q.options) && q.options.length ? q.options : ['', ''];
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className={`overflow-x-auto rounded ${hasError ? 'border border-red-400 bg-red-50 p-1' : ''}`}>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border border-gray-300 px-3 py-2 bg-gray-50 text-left font-medium text-gray-700 w-1/2">{q.placeholder || 'Item'}</th>
                <th className="border border-gray-300 px-3 py-2 bg-gray-50 text-left font-medium text-gray-700 w-1/2">{q.helpText || 'Value'}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((rowDesc, rowIdx) => {
                const letter = String.fromCharCode(97 + rowIdx);
                const rowValue = Array.isArray(value) ? (value[rowIdx] || '') : '';
                return (
                  <tr key={rowIdx}>
                    <td className="border border-gray-300 px-3 py-2 text-gray-800">
                      <span className="font-medium">{letter})</span>{rowDesc ? ` ${rowDesc}` : ''}
                    </td>
                    <td className="border border-gray-300 px-2 py-1">
                      <input type="text" value={rowValue} readOnly={ro} disabled={ro}
                        onChange={(e) => {
                          if (ro) return;
                          const current = Array.isArray(value) ? [...value] : new Array(rows.length).fill('');
                          current[rowIdx] = e.target.value;
                          onChange?.(current);
                        }} className={`w-full px-1 py-1 text-sm outline-none${disabledCls}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {errorEl}
      </div>
    );
  }

  if (family === 'data-grid' || type === 'DATA_GRID' || type === 'COMPARISON_TABLE' || type === 'DROPDOWN_GRID' || type === 'DYNAMIC_TABLE') {
    const cols = dataGridColumns(q, value);
    const cfg = resolveTypeConfig(q);
    const allowAddRows = Boolean(cfg?.allowAddRows ?? cfg?.dynamicRows);
    const minRows = Math.max(1, Number(cfg?.minRows ?? 1));
    const maxRows = Math.min(50, Math.max(minRows, Number(cfg?.maxRows ?? 15)));
    const defaultRows = Math.max(minRows, Number(cfg?.defaultRows ?? cfg?.rowCount ?? 1));

    const rawData = coerceAnswerValueForQuestion(q, value);
    let displayRows;
    if (allowAddRows) {
      if (Array.isArray(rawData) && rawData.length > 0) {
        displayRows = rawData;
      } else {
        displayRows = Array.from({ length: defaultRows }, () => Object.fromEntries(cols.map((col) => [col.key, ''])));
      }
    } else {
      const rowCount = dataGridRowCount(q);
      displayRows = Array.from({ length: rowCount }, (_, ri) => rawData[ri] || {});
    }

    const handleCellChange = (ri, colKey, cellVal) => {
      if (ro) return;
      const next = [...displayRows];
      while (next.length <= ri) {
        next.push(Object.fromEntries(cols.map((col) => [col.key, ''])));
      }
      next[ri] = { ...next[ri], [colKey]: cellVal };
      onChange?.(next);
    };

    const handleAddRow = () => {
      if (ro || displayRows.length >= maxRows) return;
      const next = [...displayRows, Object.fromEntries(cols.map((col) => [col.key, '']))];
      onChange?.(next);
    };

    const handleRemoveRow = (ri) => {
      if (ro || displayRows.length <= minRows) return;
      const next = displayRows.filter((_, idx) => idx !== ri);
      onChange?.(next);
    };

    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3 overflow-x-auto">
        {labelEl}
        <table className="w-full border-collapse text-sm border border-gray-300">
          <thead>
            <tr className="bg-gray-50">
              {cols.map((c) => (
                <th key={c.key} className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-800">
                  {c.label}
                </th>
              ))}
              {allowAddRows && !ro && (
                <th className="w-12 border border-gray-300 px-1 py-1 text-center font-medium text-gray-500 text-xs">
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'}>
                {cols.map((c) => {
                  const cellVal = row?.[c.key] || '';
                  const isDropdown = c.type === 'dropdown';

                  const selectedInOtherRows = (isDropdown && c.uniqueAcrossRows)
                    ? new Set(
                        displayRows
                          .filter((_, idx) => idx !== ri)
                          .map((r) => r?.[c.key])
                          .filter((v) => v != null && String(v).trim() !== '')
                      )
                    : null;

                  const optionsList = Array.isArray(c.options) ? c.options : [];
                  const visibleOptions = selectedInOtherRows
                    ? optionsList.filter((opt) => !selectedInOtherRows.has(opt) || opt === cellVal)
                    : optionsList;

                  return (
                    <td key={c.key} className="border border-gray-300 px-1 py-0.5">
                      {isDropdown ? (
                        <select
                          value={cellVal}
                          disabled={ro}
                          onChange={(e) => handleCellChange(ri, c.key, e.target.value)}
                          className={`w-full px-1.5 py-1 text-sm bg-white border border-gray-200 rounded outline-none focus:border-indigo-500${disabledCls}`}
                        >
                          <option value="">Select {c.label}…</option>
                          {visibleOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={cellVal}
                          readOnly={ro}
                          disabled={ro}
                          placeholder={ro ? '' : `Enter ${c.label}…`}
                          onChange={(e) => handleCellChange(ri, c.key, e.target.value)}
                          className={`w-full px-1 py-1 text-sm outline-none${disabledCls}`}
                        />
                      )}
                    </td>
                  );
                })}
                {allowAddRows && !ro && (
                  <td className="border border-gray-300 px-1 py-1 text-center">
                    <button
                      type="button"
                      disabled={displayRows.length <= minRows}
                      onClick={() => handleRemoveRow(ri)}
                      className="inline-flex items-center justify-center p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed transition-colors"
                      title={displayRows.length <= minRows ? `Minimum ${minRows} row required` : 'Remove row'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 1 .75.75v7a.75.75 0 0 1-1.5 0v-7a.75.75 0 0 1 .75-.75Zm3.59 0a.75.75 0 0 1 .75.75v7a.75.75 0 0 1-1.5 0v-7a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {allowAddRows && !ro && (
          <div className="mt-2 flex items-center justify-between">
            {displayRows.length < maxRows ? (
              <button
                type="button"
                onClick={handleAddRow}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md transition-colors cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                </svg>
                Add Row ({displayRows.length}/{maxRows})
              </button>
            ) : (
              <span className="text-xs text-amber-700 font-medium">
                Maximum {maxRows} rows limit reached
              </span>
            )}
            <span className="text-[11px] text-gray-400">
              Up to {maxRows} rows
            </span>
          </div>
        )}
        {errorEl}
      </div>
    );
  }

  if (family === 'repeating' || type === 'REPEATING_SECTION') {
    const cfg = resolveTypeConfig(q);
    const cols = dataGridColumns(q, value);
    const rows = Array.isArray(value) ? value : [{}];
    const addRow = () => {
      if (ro || rows.length >= (cfg.maxRows ?? 20)) return;
      onChange?.([...rows, Object.fromEntries(cols.map((c) => [c.key, '']))]);
    };
    const removeRow = (ri) => {
      if (ro || rows.length <= (cfg.minRows ?? 1)) return;
      onChange?.(rows.filter((_, i) => i !== ri));
    };
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="space-y-3">
          {rows.map((row, ri) => (
            <div key={ri} className="rounded border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500">Row {ri + 1}</span>
                {!ro && rows.length > (cfg.minRows ?? 1) && (
                  <button type="button" onClick={() => removeRow(ri)} className="text-xs text-red-600">Remove</button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {cols.map((c) => (
                  <div key={c.key}>
                    <label className="block text-xs text-gray-600 mb-0.5">{c.label}</label>
                    <input type="text" value={row?.[c.key] || ''} readOnly={ro} disabled={ro}
                      onChange={(e) => {
                        if (ro) return;
                        const next = [...rows];
                        next[ri] = { ...next[ri], [c.key]: e.target.value };
                        onChange?.(next);
                      }} className={inputCls} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!ro && rows.length < (cfg.maxRows ?? 20) && (
            <button type="button" onClick={addRow} className="text-sm text-indigo-600 font-medium">+ Add row</button>
          )}
        </div>
        {errorEl}
      </div>
    );
  }

  // —— Text + file attachment (single answer) ——
  if (type === 'TEXT_WITH_FILE') {
    const cfg = resolveTypeConfig(q);
    const v = normalizeTextWithFileValue(value);
    const attachmentLabel = String(cfg.attachmentLabel || 'Attachment').trim() || 'Attachment';
    const useTextarea = String(cfg.inputStyle || 'textarea').toLowerCase() !== 'text';
    const textPlaceholder = String(q?.placeholder || '').trim() || (useTextarea ? 'Enter your answer…' : 'Short answer…');

    if (ro) {
      return (
        <div key={`${keyPrefix}${q.id}`} className="mb-3">
          {labelEl}
          <DownloadableTemplateCard question={q} readOnly={ro} />
          <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-0.5">Response</p>
              <p className="text-gray-900 whitespace-pre-wrap">{v.text.trim() || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-0.5">{attachmentLabel}</p>
              <FileValueDisplay value={v.files} deliveryMode={attachmentDeliveryMode} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <DownloadableTemplateCard question={q} readOnly={ro} />
        <div className={`space-y-3 ${hasError ? 'border border-red-400 bg-red-50 p-2 rounded' : ''}`}>
          {useTextarea ? (
            <textarea
              value={v.text}
              onChange={(e) => onChange?.({ ...v, text: e.target.value })}
              placeholder={textPlaceholder}
              rows={4}
              className={inputCls}
            />
          ) : (
            <input
              type="text"
              value={v.text}
              onChange={(e) => onChange?.({ ...v, text: e.target.value })}
              placeholder={textPlaceholder}
              className={inputCls}
            />
          )}
          {!ro && <WordCountFooter question={q} text={v.text} />}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              {attachmentLabel}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="rounded-md border border-gray-200 bg-gray-50/80 px-2.5 py-2 text-[11px] text-gray-600 mb-2">
              {perQuestionUploadHint(q).map((line, li) => <div key={li}>{line}</div>)}
            </div>
            {Array.isArray(v.files) && v.files.length > 0 && (
              <div className="mb-2 p-2 rounded-lg border border-indigo-100 bg-indigo-50/40">
                <FileValueDisplay
                  value={v.files}
                  deliveryMode={attachmentDeliveryMode}
                  readOnly={ro}
                  onRemove={(fi) => {
                    const remaining = v.files.filter((_, idx) => idx !== fi);
                    onChange?.({ ...v, files: remaining });
                    if (textFileInputRef.current) textFileInputRef.current.value = '';
                  }}
                />
              </div>
            )}
            {!ro && (
              <input
                ref={textFileInputRef}
                type="file"
                multiple={Number(q?.uploadPolicy?.maxFiles || 1) > 1}
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  if (!newFiles.length) return;
                  const isMulti = Number(q?.uploadPolicy?.maxFiles || 1) > 1;
                  const maxFiles = Number(q?.uploadPolicy?.maxFiles || 1);
                  const existing = Array.isArray(v.files) ? v.files : [];
                  const combined = isMulti ? [...existing, ...newFiles].slice(0, maxFiles) : newFiles;
                  onChange?.({ ...v, files: combined });
                  e.target.value = '';
                }}
                className="mt-1 w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700"
              />
            )}
          </div>
        </div>
        {errorEl}
      </div>
    );
  }

  // —— Files ——
  if (isFileFamily(family, type)) {
    const accept = fileAccept(q, family, type);
    const capture = family === 'camera' ? 'environment' : undefined;
    const isMulti = Number(q?.uploadPolicy?.maxFiles || 1) > 1 || type === 'FILE_MULTI' || type === 'IMAGE_GALLERY';
    const maxFiles = Number(q?.uploadPolicy?.maxFiles || (isMulti ? 10 : 1));
    const currentFiles = Array.isArray(value) ? value : (value ? [value] : []);

    if (ro) {
      return (
        <div key={`${keyPrefix}${q.id}`} className="mb-3">
          {labelEl}
          <DownloadableTemplateCard question={q} readOnly={ro} />
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            <FileValueDisplay value={value} deliveryMode={attachmentDeliveryMode} readOnly />
          </div>
        </div>
      );
    }

    const handleRemoveFile = (indexToRemove) => {
      const remaining = currentFiles.filter((_, i) => i !== indexToRemove);
      setFileValidationErr(null);
      onChange?.(remaining);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <DownloadableTemplateCard question={q} readOnly={ro} />
        <div className={`space-y-2 rounded ${hasError ? 'border border-red-400 bg-red-50 p-2' : ''}`}>
          <div className="rounded-md border border-gray-200 bg-gray-50/80 px-2.5 py-2 text-[11px] text-gray-600">
            {perQuestionUploadHint(q).map((line, li) => <div key={li}>{line}</div>)}
          </div>
          {currentFiles.length > 0 && (
            <div className="p-2.5 rounded-lg border border-indigo-100 bg-indigo-50/40 space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold text-indigo-900">
                <span>Attached file{currentFiles.length > 1 ? 's' : ''}:</span>
                <span className="text-indigo-600 font-normal">{currentFiles.length} of {maxFiles} max</span>
              </div>
              <FileValueDisplay
                value={currentFiles}
                deliveryMode={attachmentDeliveryMode}
                onRemove={handleRemoveFile}
                readOnly={false}
              />
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple={isMulti}
            accept={accept}
            capture={capture}
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              const err = await validateUploadFiles(files, q);
              if (err) {
                setFileValidationErr(err);
                toast.error(err);
                e.target.value = '';
                return;
              }
              setFileValidationErr(null);
              if (isMulti) {
                const combined = [...currentFiles, ...files].slice(0, maxFiles);
                onChange?.(combined);
              } else {
                onChange?.(files);
              }
              e.target.value = '';
            }}
            className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700"
          />
        </div>
        {fileValidationErr && <p className="mt-1 text-xs text-red-600 font-medium">{fileValidationErr}</p>}
        {errorEl}
      </div>
    );
  }

  if (family === 'image-choice' || type === 'IMAGE_CHOICE') {
    const opts = normOptions(q);
    return (
      <div key={`${keyPrefix}${q.id}`} className="mb-3">
        {labelEl}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {opts.map((o, oi) => (
            <button key={oi} type="button" disabled={ro} onClick={() => !ro && onChange?.(o)}
              className={`p-3 rounded border text-sm text-center ${value === o ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
              {o}
            </button>
          ))}
        </div>
        {errorEl}
      </div>
    );
  }

  // Fallback
  return (
    <div key={`${keyPrefix}${q.id}`} className="mb-3">
      {labelEl}
      <input type="text" value={value || ''} onChange={(e) => !ro && onChange?.(e.target.value)} readOnly={ro} disabled={ro} className={inputCls} />
      {errorEl}
    </div>
  );
}
